/**
 * Cognito SSO Service
 * Handles Cognito SSO authentication flow
 */

// API Service - use production URL by default, localhost for development
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 
  (typeof window !== 'undefined' && 
   (window.location.hostname === 'sso.syberkey.com' || !window.location.hostname.includes('localhost'))
   ? 'https://api.syberkey.com' 
   : 'http://localhost:3000');

interface CognitoConfig {
  authorization_endpoint: string;
  token_endpoint: string;
  userinfo_endpoint: string;
}

/**
 * Get Cognito SSO configuration from backend
 */
export async function getCognitoConfig(): Promise<CognitoConfig> {
  try {
    console.log('[CognitoSSO] Fetching config from:', `${API_BASE_URL}/cognito-sso/config`);
    
    const response = await fetch(`${API_BASE_URL}/cognito-sso/config`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });
    
    // Check if response is JSON before parsing
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const text = await response.text();
      console.error('[CognitoSSO] Non-JSON response received:', {
        status: response.status,
        statusText: response.statusText,
        contentType: contentType,
        url: response.url,
        preview: text.substring(0, 500)
      });
      
      // Provide helpful error message
      if (response.status === 404) {
        throw new Error(`Cognito config endpoint not found. Please ensure the API server is running on ${API_BASE_URL} and the /cognito-sso/config endpoint is available.`);
      } else if (response.status === 0 || !response.ok) {
        throw new Error(`Failed to connect to API server at ${API_BASE_URL}. Please ensure the backend service is running.`);
      } else {
        throw new Error(`Server returned non-JSON response (${response.status}). This might be a routing or endpoint issue. Check that the API server is running and accessible.`);
      }
    }
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`Failed to fetch Cognito config: ${response.status} - ${errorData.message || response.statusText}`);
    }
    
    const config = await response.json();
    console.log('[CognitoSSO] Config received successfully:', config);
    return config;
  } catch (error) {
    console.error('[CognitoSSO] Error fetching config:', error);
    
    // Provide more helpful error messages
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new Error(`Network error: Unable to connect to API server at ${API_BASE_URL}. Please ensure the backend service is running on port 3000.`);
    }
    
    throw error;
  }
}

/**
 * Generate random state for CSRF protection
 */
function generateState(): string {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
}

/**
 * Initiate Cognito SSO authorization flow
 */
export async function initiateCognitoSSO(): Promise<void> {
  try {
    console.log('[CognitoSSO] Initiating Cognito SSO flow...');
    console.log('[CognitoSSO] API Base URL:', API_BASE_URL);
    
    // Get Cognito configuration
    const config = await getCognitoConfig();
    console.log('[CognitoSSO] Config received:', config);
    
    // Generate state for CSRF protection
    const state = generateState();
    localStorage.setItem('cognito_sso_state', state);
    console.log('[CognitoSSO] State generated and stored:', state);
    
    // Determine redirect URI based on environment
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const redirectUri = isLocalhost 
      ? `${window.location.protocol}//${window.location.host}/callback`
      : `https://sso.syberkey.com/callback`;
    
    // Cognito Client ID (public client)
    const clientId = '5u5innfog6g34ii2etusfrj0dh';
    
    // Build authorization URL
    const params = new URLSearchParams({
      response_type: 'code',
      client_id: clientId,
      redirect_uri: redirectUri,
      scope: 'openid email profile',
      state: state
    });
    
    const authUrl = `${config.authorization_endpoint}?${params.toString()}`;
    
    console.log('[CognitoSSO] Redirecting to Cognito Hosted UI:', authUrl);
    console.log('[CognitoSSO] Redirect URI:', redirectUri);
    console.log('[CognitoSSO] State:', state);
    
    // Redirect to Cognito Hosted UI
    window.location.href = authUrl;
  } catch (error) {
    console.error('[CognitoSSO] Error initiating SSO:', error);
    throw error;
  }
}
